import React, { useState } from 'react';
import { apiService } from '../../services/api';
import { DisciplineState, intentLabel } from '../../types/discipline';

/**
 * Discipline intent — the 3-way enum that drives which rule set the
 * Discipline engine (PME v3.0) applies to this position. Each value maps
 * to a school of thought:
 *   - 'hold'    → Keep My Shares  (BCI; assignment is unwanted, defend)
 *   - 'wheel'   → Cycle for Income (r/thetagang Wheel; assignment is goal)
 *   - 'release' → Harvest Premium  (tastytrade/OA; close before assign)
 */
export type Intent = 'hold' | 'wheel' | 'release';

interface PositionIntentChipProps {
  positionId: string;
  /** Current per-position intent. */
  value: Intent;
  /** True for a cash-secured put — flips the conservative 'hold' label to
   *  "Stay in Cash" (a CSP holds no shares to keep). Covered call = false. */
  isPut?: boolean;
  /** Called immediately on click with the user's optimistic choice. */
  onChange: (next: Intent) => void;
  /** BAB-66 race fix: called with the server's authoritative DisciplineState
   *  returned from the PATCH response. Parent should `setDiscipline(d)` here
   *  INSTEAD of refetching via GET /discipline (which would race the commit
   *  and read the OLD intent — same `intent` + same rule could end up
   *  yielding different actions across consecutive flips). */
  onDisciplineRefresh?: (discipline: DisciplineState) => void;
}

/**
 * PositionIntentChip — BAB-71: collapsed single-value pill that opens a
 * picker on tap. Evolved from BAB-60's always-expanded 3-segment control,
 * which a user read as a recommendation rather than their own editable
 * choice. Now the card shows ONLY the selected intent (a pill in the
 * school's color, with a caret signalling it's tappable); tapping opens a
 * small menu of the three stances to switch.
 *
 * Per the founder's intent-school decision (project_discipline_framework_may2026):
 * each option names the user's school explicitly. "Cycle for Income" is the
 * thetagang Wheel cohort; "Harvest Premium" is the tastytrade/OA premium-
 * seller cohort; "Keep My Shares" is the BCI long-term-holder cohort.
 *
 * Design notes:
 *   - No emoji (per reddit-cc-advisor: emoji = consumer-fintech tell)
 *   - Plain-English blurbs (per plain_english_default) — the user owns this
 *     stance; copy never reads as advice.
 *   - Active pill carries the school's color (green/amber/indigo).
 */
const PositionIntentChip: React.FC<PositionIntentChipProps> = ({ positionId, value, onChange, onDisciplineRefresh, isPut = false }) => {
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const flipTo = async (target: Intent, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    if (busy) return;
    if (target === value) return;

    setBusy(true);
    setErrorMsg(null);
    onChange(target);  // optimistic
    try {
      const res = await apiService.updatePositionIntent(positionId, target);
      if (res.intent !== target) {
        onChange(res.intent as Intent);
      }
      // BAB-66 race fix: hand the parent the server's fresh DisciplineState
      // (it was computed AFTER the commit, so it's authoritative). Parent
      // should consume this directly rather than firing GET /discipline,
      // which would race the commit and risk reading the OLD intent.
      if (onDisciplineRefresh && res.discipline) {
        onDisciplineRefresh(res.discipline);
      }
    } catch (err) {
      onChange(value);
      setErrorMsg('Could not update intent — try again');
      setTimeout(() => setErrorMsg(null), 3000);
    } finally {
      setBusy(false);
    }
  };

  // Per-school palette — distinct from PME action palette so the
  // user's "stance" is visually separate from the engine's "verdict".
  const SCHOOL_PALETTE: Record<Intent, { fg: string; bg: string; border: string; label: string; blurb: string }> = {
    hold: {
      fg: '#6EE7B7',
      bg: 'rgba(16, 185, 129, 0.18)',
      border: 'rgba(16, 185, 129, 0.45)',
      label: 'Keep My Shares',
      blurb: 'Assignment is unwanted — defend the stock.',
    },
    wheel: {
      fg: '#A78BFA',
      bg: 'rgba(167, 139, 250, 0.18)',
      border: 'rgba(167, 139, 250, 0.45)',
      label: 'Cycle for Income',
      blurb: 'Fine being assigned — keep cycling premium.',
    },
    release: {
      fg: '#FCD34D',
      bg: 'rgba(245, 158, 11, 0.18)',
      border: 'rgba(245, 158, 11, 0.45)',
      label: 'Harvest Premium',
      blurb: 'Just here for the premium — close before assignment.',
    },
  };

  const active = SCHOOL_PALETTE[value];
  const intents: Intent[] = ['hold', 'wheel', 'release'];

  return (
    <div
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
      onClick={(e) => e.stopPropagation()}
    >
      <span style={{ fontSize: '10px', fontWeight: 500, color: '#9CA3AF', letterSpacing: '0.02em' }}>
        Intent
      </span>

      {/* Collapsed pill — shows only the selected stance; tap to open the picker. */}
      <button
        type="button"
        onClick={() => !busy && setOpen((o) => !o)}
        disabled={busy}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={errorMsg || active.blurb}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '3px 8px 3px 10px',
          fontSize: '11px',
          fontWeight: 700,
          color: active.fg,
          background: active.bg,
          border: `1px solid ${active.border}`,
          borderRadius: '999px',
          cursor: busy ? 'wait' : 'pointer',
          opacity: busy ? 0.7 : 1,
          transition: 'opacity 0.15s ease',
          whiteSpace: 'nowrap',
        }}
      >
        {intentLabel(value, isPut)}
        <span
          style={{
            fontSize: '8px',
            opacity: 0.8,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.15s ease',
          }}
        >
          ▼
        </span>
      </button>

      {errorMsg && (
        <span style={{ fontSize: '10px', color: '#FCA5A5', whiteSpace: 'nowrap' }}>{errorMsg}</span>
      )}

      {open && (
        <>
          {/* Click-away backdrop. */}
          <div
            onClick={() => setOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 40 }}
          />
          {/* Picker menu. */}
          <div
            role="listbox"
            style={{
              position: 'absolute',
              top: 'calc(100% + 6px)',
              left: 0,
              zIndex: 41,
              width: '248px',
              background: '#0B1220',
              border: '1px solid #334155',
              borderRadius: '10px',
              boxShadow: '0 12px 28px rgba(0,0,0,0.5)',
              padding: '4px',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
            }}
          >
            {intents.map((intent) => {
              const p = SCHOOL_PALETTE[intent];
              const isSel = intent === value;
              return (
                <button
                  key={intent}
                  type="button"
                  role="option"
                  aria-selected={isSel}
                  onClick={(e) => flipTo(intent, e)}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.05)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = isSel ? p.bg : 'transparent'; }}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                    textAlign: 'left',
                    padding: '8px 10px',
                    border: `1px solid ${isSel ? p.border : 'transparent'}`,
                    borderRadius: '7px',
                    background: isSel ? p.bg : 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ width: '12px', flexShrink: 0, color: p.fg, fontSize: '11px', lineHeight: '15px' }}>
                    {isSel ? '✓' : ''}
                  </span>
                  <span>
                    <span style={{ display: 'block', color: isSel ? p.fg : '#E5E7EB', fontSize: '12px', fontWeight: isSel ? 700 : 600 }}>
                      {intentLabel(intent, isPut)}
                    </span>
                    <span style={{ display: 'block', color: '#94A3B8', fontSize: '11px', marginTop: '1px', lineHeight: 1.35 }}>
                      {p.blurb}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default PositionIntentChip;
